import {
  getLeoUuid,
  LeoUUID,
  getString,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
/* eslint-disable import/export */
export abstract class GenerateGenericSurveyLinkRPC implements
  LeoRPC<GenerateGenericSurveyLinkRPC.Request, GenerateGenericSurveyLinkRPC.Response, GenerateGenericSurveyLinkRPC.Errors.Errors>
{
  abstract execute(
    request: GenerateGenericSurveyLinkRPC.Request
  ): Promise<LeoRPCResult<GenerateGenericSurveyLinkRPC.Response, GenerateGenericSurveyLinkRPC.Errors.Errors>>;
}

export namespace GenerateGenericSurveyLinkRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    surveyId: string;
    projectId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGenerateGenericSurveyLinkRPCRequest: boolean = true;
  
    readonly surveyId: LeoUUID;
    readonly projectId: LeoUUID;
  
  
    constructor(
    surveyId: LeoUUID, 
    projectId: LeoUUID
    ) {
      this.surveyId = surveyId;
      this.projectId = projectId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      return new Request(
        surveyId,
        projectId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          surveyId: this.surveyId.toDTO(),
          projectId: this.projectId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `GenerateGenericSurveyLinkRPC(surveyId='${this.surveyId}',projectId='${this.projectId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    liveURL: string;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGenerateGenericSurveyLinkResponse: boolean = true;
  
    readonly liveURL: string;
  
  
    constructor(
    liveURL: string
    ) {
      Response.validateLiveUrl(liveURL);
      this.liveURL = liveURL;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const liveURL = getString(dto, "liveURL", InvalidResponseLiveUrlError);
      return new Response(
        liveURL
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          liveURL: this.liveURL
      };
      return dto;
    }
    private static validateLiveUrl = (liveURL: string): void => {
      if(liveURL.trim().length === 0) {
        throw new InvalidResponseLiveUrlNonEmptyError(
          `Attribute liveURL cannot be an empty string.`
        );
      }
    }
    
  }
  
  Response.prototype.toString = function toString(): string {
    return `GenerateGenericSurveyLink(liveURL='${this.liveURL}')`;
  };
  

  export enum RPCError {
    InvalidSurveyId = "INVALID_SURVEY_ID",
    SurveyNotActive = "SURVEY_NOT_ACTIVE",
    InvalidProjectId = "INVALID_PROJECT_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED"
  }

  export namespace Errors {
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `GenerateGenericSurveyLinkRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface SurveyNotActiveDTO {
      code: string;
    }
    
    export class SurveyNotActive extends LeoRPCError<RPCError> {
      readonly isSurveyNotActive: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.SurveyNotActive;
        super(code);
      }
    
    
      toDTO(): SurveyNotActiveDTO {
        const dto: SurveyNotActiveDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    SurveyNotActive.prototype.toString = function toString(): string {
      return `GenerateGenericSurveyLinkRPC.SURVEY_NOT_ACTIVE()`;
    };
    
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `GenerateGenericSurveyLinkRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `GenerateGenericSurveyLinkRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    

    export type Errors = InvalidSurveyId | SurveyNotActive | InvalidProjectId | ProjectAlreadyArchived;
    export type ErrorsDTO = InvalidSurveyIdDTO | SurveyNotActiveDTO | InvalidProjectIdDTO | ProjectAlreadyArchivedDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.SurveyNotActive:
          return new SurveyNotActive();
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestSurveyIdError extends GenerateGenericSurveyLinkRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestProjectIdError extends GenerateGenericSurveyLinkRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidResponseLiveUrlError extends GenerateGenericSurveyLinkRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseLiveUrlError";
  }
}

export class InvalidResponseLiveUrlNonEmptyError extends InvalidResponseLiveUrlError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseLiveUrlNonEmptyError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends GenerateGenericSurveyLinkRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseSurveyNotActiveError extends GenerateGenericSurveyLinkRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyNotActiveError";
  }
}

export class InvalidResponseInvalidProjectIdError extends GenerateGenericSurveyLinkRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends GenerateGenericSurveyLinkRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

